// Ignore codes from webpack-plugin-serve
const ignoreCodes = [1008, 1011];
const retryAttempts = 120;
const pingDelayMs = 3 * 60 * 1000;

type EventHandlerProps =
    | ['open', () => void]
    | ['message', (message: MessageEvent) => void]
    | ['close', () => void]
    | ['error', (error: Event) => void];

class WebClientSocket {
    args: { address: string };
    attempts: number;
    eventHandlers: EventHandlerProps[];
    retrying: boolean;
    destroyed: boolean;
    socket: WebSocket | undefined;
    connecting: boolean;
    pingInterval: NodeJS.Timeout | null;

    constructor(opts: { address: string }) {
        this.args = opts;
        this.attempts = 0;
        this.eventHandlers = [];
        this.retrying = false;
        this.destroyed = false;
        this.connecting = false;
        this.pingInterval = null;

        this.connect();
    }

    get readyState(): number | undefined {
        return this.socket?.readyState;
    }

    send(message: string): void {
        this.socket?.send(message);
    }

    sendWhenPossible(message: string, tries: number): void {
        if (tries === 0) {
            console.error('RFCP: Socket could not send message');
            return;
        }

        if (this.socket?.readyState === WebSocket.OPEN) {
            this.send(message);
        } else {
            setTimeout(this.sendWhenPossible.bind(this, message, tries - 1), 1000);
        }
    }

    addEventListener(...args: EventHandlerProps): void {
        this.eventHandlers.push(args);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.socket?.addEventListener(...args);
    }

    close(): void {
        this.socket?.close();
    }

    destroy(): void {
        this.destroyed = true;
        this.close();
    }

    connect(): void {
        if (this.socket) {
            delete this.socket;
        }

        this.destroyed = false;
        this.connecting = true;

        this.socket = new WebSocket(this.args.address);

        this.socket.addEventListener('close', (event) => {
            this.stopAutoPing();

            if (this.destroyed || ignoreCodes.includes(event.code)) {
                return;
            }

            if (!this.retrying) {
                console.warn(`RFCP: Socket was closed and will attempt to reconnect`);
            }

            this.reconnect();
        });

        this.socket.addEventListener('open', () => {
            this.attempts = 0;
            this.retrying = false;
            this.startAutoPing();
        });

        if (this.eventHandlers.length) {
            for (const [name, fn] of this.eventHandlers) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                this.socket.addEventListener(name, fn);
            }
        }
    }

    ping(): void {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send('ping');
        }
    }

    startAutoPing(): void {
        this.pingInterval = setInterval(this.ping.bind(this), pingDelayMs);
    }

    stopAutoPing(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    reconnect(): void {
        this.attempts += 1;
        this.retrying = true;

        if (this.attempts > retryAttempts) {
            console.error(`RFCP: Socket could not be reconnected.`);
            this.retrying = false;
            return;
        }

        setTimeout(() => this.connect(), 2000);
    }

    removeEventListener(...args: EventHandlerProps): void {
        const [, handler] = args;
        this.eventHandlers = this.eventHandlers.filter(([, fn]) => fn === handler);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.socket?.removeEventListener(...args);
    }
}
export default WebClientSocket;
